import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { NewsCard } from '@/components/base/NewsCard';
import { newsArticles, categories } from '@/mocks/news';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const fallbackArticle = newsArticles[0];
  const article = newsArticles.find((a) => a.id === Number(id)) || fallbackArticle;

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen bg-background-50 pt-20 flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-4">文章不存在</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-400 transition-colors">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = newsArticles.filter((a) => a.id !== article.id && a.category === article.category).slice(0, 3);
  const otherArticles = newsArticles.filter((a) => a.id !== article.id && a.category !== article.category).slice(0, 3);

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'http://mssdp.cn/',
      },
      ...(category
        ? [
            {
              '@type': 'ListItem' as const,
              position: 2,
              name: category.name,
              item: 'http://mssdp.cn/',
            },
          ]
        : []),
      {
        '@type': 'ListItem',
        position: category ? 3 : 2,
        name: article.title,
        item: `http://mssdp.cn/article/${article.id}`,
      },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.publishDate,
    image: article.imageUrl,
    publisher: {
      '@type': 'Organization',
      name: '每日大赛黑料网',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `http://mssdp.cn/article/${article.id}`,
    },
  };

  return (
    <>
      <Navbar />
      <main>
        <section className="relative w-full h-[360px] md:h-[500px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/70 to-black/40"></div>
          <div className="absolute inset-0 flex items-end">
            <div className="w-full max-w-[1440px] mx-auto px-4 md:px-6 pb-10 md:pb-14">
              <div className="max-w-3xl">
                <h1 className="font-heading text-2xl md:text-4xl font-black text-foreground-950 leading-tight">
                  {article.title}
                </h1>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-8 mt-6">
                  <p className="text-sm text-foreground-600 leading-relaxed max-w-lg">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center gap-4 text-xs text-foreground-500 sm:mt-2">
                    <span className="whitespace-nowrap">{article.author}</span>
                    <span className="whitespace-nowrap">{article.publishDate}</span>
                    <span className="whitespace-nowrap">
                      <i className="ri-eye-line mr-1"></i>{(article.readCount / 10000).toFixed(1)}万阅读
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-100 py-10 md:py-16">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', path: '/' },
                  { label: category?.name || '分类', path: category ? '/' : undefined },
                  { label: article.title },
                ]}
              />

              <div className="flex flex-wrap gap-2 mt-6 mb-10">
                {article.tags.map((tag) => (
                  <span key={tag} className="text-xs px-3 py-1 rounded-full bg-primary-500/15 text-primary-400 whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>

              <article className="prose-custom">
                {article.content.map((block, index) => {
                  if (block.type === 'heading') {
                    return (
                      <h2 key={index} className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4">
                        {block.text}
                      </h2>
                    );
                  }
                  return (
                    <p key={index} className="text-base text-foreground-600 leading-relaxed mb-5">
                      {block.text}
                    </p>
                  );
                })}
              </article>

              <div className="flex items-center justify-between mt-12 pt-8 border-t border-background-300/60">
                <div className="flex gap-2">
                  <button className="w-9 h-9 flex items-center justify-center rounded-full border border-background-300 text-foreground-500 hover:text-primary-500 hover:border-primary-500/40 transition-all duration-200" aria-label="分享到微博">
                    <i className="ri-weibo-line"></i>
                  </button>
                  <button className="w-9 h-9 flex items-center justify-center rounded-full border border-background-300 text-foreground-500 hover:text-primary-500 hover:border-primary-500/40 transition-all duration-200" aria-label="分享到微信">
                    <i className="ri-wechat-line"></i>
                  </button>
                  <button className="w-9 h-9 flex items-center justify-center rounded-full border border-background-300 text-foreground-500 hover:text-primary-500 hover:border-primary-500/40 transition-all duration-200" aria-label="复制链接">
                    <i className="ri-link"></i>
                  </button>
                </div>
                <span className="text-xs text-foreground-500">{article.author} · {article.publishDate}</span>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-50 py-10 md:py-16">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-black text-foreground-950 mb-8">
              相关爆料
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {relatedArticles.concat(otherArticles).slice(0, 3).map((news) => (
                <NewsCard
                  key={news.id}
                  id={news.id}
                  title={news.title}
                  excerpt={news.excerpt}
                  imageUrl={news.imageUrl}
                  author={news.author}
                  publishDate={news.publishDate}
                  readCount={news.readCount}
                  tags={news.tags}
                  variant="vertical"
                  size="small"
                />
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
    </>
  );
}